import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type UnitKey } from '@fewerbags/core';
import type { GroceryItemDto } from '@fewerbags/schema';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { UNIT_OPTIONS } from '../../lib/units';
import { groceryKeys } from '../../pages/groceries/queries';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { Field } from '../Field';
import { SelectField } from '../SelectField';

/** Edit a list item's buy quantity/unit/note, or remove it from the list. */
export function EditGroceryItemDialog({
  open,
  onClose,
  listId,
  item,
}: {
  open: boolean;
  onClose: () => void;
  listId: string;
  item: GroceryItemDto | null;
}) {
  const qc = useQueryClient();
  const [qty, setQty] = useState('0');
  const [unit, setUnit] = useState<UnitKey>('count');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!item) return;
    setQty(String(item.buyQty));
    setUnit(item.unit);
    setNote(item.note ?? '');
  }, [item]);

  const invalidate = () => qc.invalidateQueries({ queryKey: groceryKeys.detail(listId) });

  const update = useMutation({
    mutationFn: (input: Parameters<typeof api.groceries.items.update>[2]) =>
      api.groceries.items.update(listId, item!.id, input),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.groceries.items.remove(listId, item!.id),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
  });

  if (!item) {
    return (
      <Dialog open={open} onClose={onClose} title="Item">
        <p className="text-sm text-muted">No item selected.</p>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} title={item.ingredientName}>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <Field
            id="grocery-edit-qty"
            label="Quantity"
            type="number"
            inputMode="decimal"
            step="any"
            min="0"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
          <SelectField
            id="grocery-edit-unit"
            label="Unit"
            options={UNIT_OPTIONS}
            value={unit}
            onChange={(e) => setUnit(e.target.value as UnitKey)}
          />
        </div>
        <Field
          id="grocery-edit-note"
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {update.isError ? <Alert tone="error">{messageForError(update.error)}</Alert> : null}
        <Button
          onClick={() => {
            const qtyNum = Number(qty);
            update.mutate({
              buyQty: Number.isFinite(qtyNum) && qtyNum >= 0 ? qtyNum : item.buyQty,
              unit,
              note: note.trim() ? note.trim() : null,
            });
          }}
          disabled={update.isPending}
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>

        <div className="border-t border-line pt-4">
          {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() => {
              if (confirm(`Remove ${item.ingredientName} from the list?`)) remove.mutate();
            }}
          >
            Remove from list
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
